import React, { useState } from "react";
import {
	Card,
	CardContent,
	Typography,
	Box,
	Button,
	List,
	ListItem,
	ListItemText,
	ListItemSecondaryAction,
	IconButton,
	TextField,
	Grid,
	Chip,
	Divider,
} from "@material-ui/core";
import {
	Plus as AddIcon,
	Trash2 as DeleteIcon,
	Home as InteriorIcon,
	Map as ExteriorIcon,
} from "react-feather";

interface RecintoItem {
	id: string;
	text: string;
	type: 'interior' | 'exterior';
}

export const LegalScopeTab = () => {
	const [interiorRecintos, setInteriorRecintos] = useState<RecintoItem[]>([]);
	const [exteriorRecintos, setExteriorRecintos] = useState<RecintoItem[]>([]);
	const [newInteriorText, setNewInteriorText] = useState("");
	const [newExteriorText, setNewExteriorText] = useState("");
	
	// Free text fields
	const [text1, setText1] = useState("");
	const [text2, setText2] = useState("");
	const [text3, setText3] = useState("");
	const [text4, setText4] = useState("");

	const addInteriorRecinto = () => {
		if (newInteriorText.trim()) {
			const newRecinto: RecintoItem = {
				id: Date.now().toString(),
				text: newInteriorText.trim(),
				type: 'interior',
			};
			setInteriorRecintos([...interiorRecintos, newRecinto]);
			setNewInteriorText("");
		}
	};

	const addExteriorRecinto = () => {
		if (newExteriorText.trim()) {
			const newRecinto: RecintoItem = {
				id: Date.now().toString(),
				text: newExteriorText.trim(),
				type: 'exterior',
			};
			setExteriorRecintos([...exteriorRecintos, newRecinto]);
			setNewExteriorText("");
		}
	};

	const deleteInteriorRecinto = (id: string) => {
		setInteriorRecintos(interiorRecintos.filter(recinto => recinto.id !== id));
	};

	const deleteExteriorRecinto = (id: string) => {
		setExteriorRecintos(exteriorRecintos.filter(recinto => recinto.id !== id));
	};

	return (
		<Card>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Alcances Legales
				</Typography>
				
				<Grid container spacing={3}>
					{/* Interior Recintos Section */}
					<Grid item xs={12} md={6}>
						<Box>
							<Typography variant="subtitle1" gutterBottom>
								<Chip 
									icon={<InteriorIcon />} 
									label="Interior" 
									color="primary" 
									size="small" 
									style={{ marginRight: 8 }} 
								/>
								Recintos Interiores
							</Typography>
							
							<Grid container spacing={1} alignItems="center" style={{ marginBottom: 16 }}>
								<Grid item xs={8}>
									<TextField
										fullWidth
										variant="outlined"
										size="small"
										placeholder="Nuevo recinto interior..."
										value={newInteriorText}
										onChange={(e) => setNewInteriorText(e.target.value)}
										onKeyPress={(e) => {
											if (e.key === 'Enter') {
												addInteriorRecinto();
											}
										}}
									/>
								</Grid>
								<Grid item xs={4}>
									<Button
										variant="contained"
										color="primary"
										startIcon={<AddIcon />}
										onClick={addInteriorRecinto}
										fullWidth
										size="small"
										disabled={!newInteriorText.trim()}
									>
										Agregar
									</Button>
								</Grid>
							</Grid>

							<List dense>
								{interiorRecintos.map((recinto, index) => (
									<ListItem key={recinto.id} divider={index < interiorRecintos.length - 1}>
										<ListItemText 
											primary={recinto.text}
											primaryTypographyProps={{ variant: 'body2' }}
										/>
										<ListItemSecondaryAction>
											<IconButton
												edge="end"
												onClick={() => deleteInteriorRecinto(recinto.id)}
												color="secondary"
												size="small"
											>
												<DeleteIcon fontSize="small" />
											</IconButton>
										</ListItemSecondaryAction>
									</ListItem>
								))}
							</List>

							{interiorRecintos.length === 0 && (
								<Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', padding: 16 }}>
									No hay recintos interiores agregados
								</Typography>
							)}
						</Box>
					</Grid>

					{/* Exterior Recintos Section */}
					<Grid item xs={12} md={6}>
						<Box>
							<Typography variant="subtitle1" gutterBottom>
								<Chip 
									icon={<ExteriorIcon />} 
									label="Exterior" 
									color="secondary" 
									size="small" 
									style={{ marginRight: 8 }} 
								/>
								Recintos Exteriores
							</Typography>
							
							<Grid container spacing={1} alignItems="center" style={{ marginBottom: 16 }}>
								<Grid item xs={8}>
									<TextField
										fullWidth
										variant="outlined"
										size="small"
										placeholder="Nuevo recinto exterior..."
										value={newExteriorText}
										onChange={(e) => setNewExteriorText(e.target.value)}
										onKeyPress={(e) => {
											if (e.key === 'Enter') {
												addExteriorRecinto();
											}
										}}
									/>
								</Grid>
								<Grid item xs={4}>
									<Button
										variant="contained"
										color="secondary"
										startIcon={<AddIcon />}
										onClick={addExteriorRecinto}
										fullWidth
										size="small"
										disabled={!newExteriorText.trim()}
									>
										Agregar
									</Button>
								</Grid>
							</Grid>

							<List dense>
								{exteriorRecintos.map((recinto, index) => (
									<ListItem key={recinto.id} divider={index < exteriorRecintos.length - 1}>
										<ListItemText 
											primary={recinto.text}
											primaryTypographyProps={{ variant: 'body2' }}
										/>
										<ListItemSecondaryAction>
											<IconButton
												edge="end"
												onClick={() => deleteExteriorRecinto(recinto.id)}
												color="secondary"
												size="small"
											>
												<DeleteIcon fontSize="small" />
											</IconButton>
										</ListItemSecondaryAction>
									</ListItem>
								))}
							</List>

							{exteriorRecintos.length === 0 && (
								<Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', padding: 16 }}>
									No hay recintos exteriores agregados
								</Typography>
							)}
						</Box>
					</Grid>
				</Grid>

				<Divider style={{ margin: '24px 0' }} />

				{/* Free Text Fields Section */}
				<Box>
					<Typography variant="subtitle1" gutterBottom>
						Textos Libres
					</Typography>
					
					<Grid container spacing={2}>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								variant="outlined"
								label="Texto 1"
								multiline
								rows={3}
								value={text1}
								onChange={(e) => setText1(e.target.value)}
								placeholder="Ingrese texto libre 1..."
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								variant="outlined"
								label="Texto 2"
								multiline
								rows={3}
								value={text2}
								onChange={(e) => setText2(e.target.value)}
								placeholder="Ingrese texto libre 2..."
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								variant="outlined"
								label="Texto 3"
								multiline
								rows={3}
								value={text3}
								onChange={(e) => setText3(e.target.value)}
								placeholder="Ingrese texto libre 3..."
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								variant="outlined"
								label="Texto 4"
								multiline
								rows={3}
								value={text4}
								onChange={(e) => setText4(e.target.value)}
								placeholder="Ingrese texto libre 4..."
							/>
						</Grid>
					</Grid>
				</Box>
			</CardContent>
		</Card>
	);
};